import React from "react";
import dayjs from "dayjs";
import {
  Box,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import SectionCard from "./SectionCard";
import ItemTypePicker, { ORNAMENT_PCS_INPUT_ID } from "./ItemTypePicker";
import {
  BG,
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  BLUE_MED,
  BLUE_PALE,
  BLUE_SUBTLE,
  BORDER,
  ERROR_BG,
  ERROR_C,
  INFO_C,
  INK,
  INK_MUTED,
  INK_SOFT,
  SURFACE,
  fieldSx,
  getFieldSx,
} from "./shared";

// ─── Jewellery / Ornaments section (step 2) ──────────────────────────────────
// Presentational block extracted verbatim from PaymentScreen. State, handlers
// and the parent's local selectSx/labelSx style objects arrive here as props.
const OrnamentsSection = ({
  stepStateOf,
  openSections,
  toggleSection,
  sectionSummary,
  paymentLocked,
  ornaments,
  selectedMetalId,
  handleMetalTypeChange,
  metalTypes,
  setCurrentOrnament,
  currentOrnament,
  ratePerGram,
  rateSource,
  loanDate,
  handleRateChange,
  grossAtNetSyncRef,
  showSnackbar,
  addOrnamentBtnRef,
  setValueEdited,
  editingIndex,
  addOrnament,
  errors,
  isMobile,
  editOrnament,
  deleteOrnament,
  formData,
  selectSx,
  labelSx,
  itemTypes,
  onItemTypeCreated,
  itemTypeError,
  itemTypeBranchId,
  storeId,
  userId,
  itemTypePickerRef,
  onItemTypeCreateCancelled,
}) => {
  // Touch devices (phones/tablets) rather than a breakpoint: what matters here
  // is that the keyboard is a soft one, not how wide the screen is.
  const isTouch = useMediaQuery("(pointer: coarse)");
  // When the user last physically touched the Net field. Android's soft-keyboard
  // "Next" key advances focus into Net natively (no keydown reaches us), which
  // looks identical to a tap on Net unless we remember the tap.
  const netTapAtRef = React.useRef(0);

  // The caption under the Rate field carries the two things the number does not
  // say for itself: that it is the PURE-metal rate (the ornament's Purity % is
  // applied on top), and — when the loan date has no rate of its own — the
  // earlier date it was carried forward from. Without the second half a teller
  // keying an old loan cannot tell a figure they just set from one that belongs
  // to another day.
  const rateHint = React.useMemo(() => {
    const carriedFrom =
      ratePerGram &&
      rateSource?.date &&
      !rateSource.exact &&
      rateSource.date !== loanDate
        ? dayjs(rateSource.date).format("DD/MM/YY")
        : null;
    // Only the carried-forward case is worth colour — the standing reminder
    // that the rate is the pure one stays quiet.
    return carriedFrom
      ? { text: `100% · from ${carriedFrom}`, tone: INFO_C }
      : { text: "for 100% purity", tone: INK_MUTED };
  }, [ratePerGram, rateSource, loanDate]);

  // Copies gross → net, which in turn lets the ornament value auto-calculate.
  // Only overwrite net when gross actually changed since it last drove net —
  // re-committing an unchanged gross must not clobber an edited net.
  const syncNetFromGross = ({ requireUntouchedNet = false } = {}) => {
    const gross = parseFloat(currentOrnament.grossWeight) || 0;
    if (gross === grossAtNetSyncRef.current) return false;
    if (requireUntouchedNet) {
      // An incidental blur (unlike a deliberate Enter/Next) must never
      // overwrite a net the user typed themselves — only one that is still
      // blank or still holds whatever gross last put there.
      const net = parseFloat(currentOrnament.netWeight) || 0;
      if (net !== 0 && net !== grossAtNetSyncRef.current) return false;
    }
    setCurrentOrnament((prev) => ({ ...prev, netWeight: gross }));
    grossAtNetSyncRef.current = gross;
    showSnackbar("Net weight auto-filled from gross", "info");
    return true;
  };

  const focusAddButton = () => {
    setTimeout(() => {
      addOrnamentBtnRef.current?.focus();
    }, 0);
  };

  return (
                <SectionCard
                  icon="📿"
                  title="Jewellery / Ornaments"
                  step={2}
                  stepState={stepStateOf(2)}
                  collapsed={!openSections[2]}
                  onToggle={() => toggleSection(2)}
                  summary={sectionSummary[2]}
                  readOnly={paymentLocked}
                >
                  {/* Input Row */}
                  <Box
                    id="ornament-form"
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: BLUE_PALE,
                      border: `1px solid ${BLUE_LIGHT}`,
                      borderRadius: "10px",
                    }}
                  >
                    {/* Item count + total net weight chip */}
                    {ornaments.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={`${ornaments.length} item${
                            ornaments.length !== 1 ? "s" : ""
                          } · ${ornaments
                            .reduce(
                              (s, o) => s + (parseFloat(o.netWeight) || 0),
                              0,
                            )
                            .toFixed(3)} g net`}
                          size="small"
                          sx={{
                            bgcolor: BLUE_LIGHT,
                            color: BLUE_DARK,
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            border: `1px solid ${BLUE_MED}44`,
                          }}
                        />
                      </Box>
                    )}

                    {/* ── Add-item form: all controls on a single row ─────── */}
                    <Grid
                      container
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ mb: 1 }}
                    >
                      <Grid item lg={1.4} md={3} sm={6} xs={6}>
                        <FormControl
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                          }}
                        >
                          <InputLabel sx={labelSx}>Metal Type</InputLabel>
                          <Select
                            value={selectedMetalId}
                            label="Metal Type"
                            onChange={(e) =>
                              handleMetalTypeChange(e.target.value)
                            }
                            sx={selectSx}
                          >
                            {metalTypes?.map((m) => (
                              <MenuItem key={m.id} value={m.id}>
                                {m.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item lg={1.5} md={2} sm={3} xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Rate (₹/g)"
                          type="number"
                          value={ratePerGram}
                          onChange={handleRateChange}
                          sx={fieldSx}
                          inputProps={{ min: 0, step: "any" }}
                          helperText={rateHint.text}
                          FormHelperTextProps={{
                            sx: {
                              m: 0,
                              mt: 0.25,
                              fontSize: "0.65rem",
                              lineHeight: 1.25,
                              color: rateHint.tone,
                            },
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "Minus") {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Grid>

                      <Grid item lg={1.15} md={2} sm={3} xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Purity %"
                          type="number"
                          value={currentOrnament.purity}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (value === "") {
                              setCurrentOrnament((prev) => ({
                                ...prev,
                                purity: "",
                              }));
                              return;
                            }
                            let num = parseFloat(value);
                            if (!isNaN(num) && num >= 0) {
                              setCurrentOrnament((prev) => ({
                                ...prev,
                                purity: num,
                              }));
                            } else if (num < 0) {
                              setCurrentOrnament((prev) => ({
                                ...prev,
                                purity: 0,
                              }));
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "Minus") {
                              e.preventDefault();
                            }
                          }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid item lg={1.75} md={5} sm={6} xs={12}>
                        {/* Picked from Item Type Master for this metal — by
                            name or short code — or created on the spot. */}
                        <ItemTypePicker
                          ref={itemTypePickerRef}
                          onCreateCancelled={onItemTypeCreateCancelled}
                          allItemTypes={itemTypes}
                          metalId={currentOrnament.metal_type_id}
                          metalName={currentOrnament.metal}
                          itemTypeId={currentOrnament.item_type_id}
                          itemName={currentOrnament.itemName}
                          onPick={(row) =>
                            setCurrentOrnament((prev) => ({
                              ...prev,
                              item_type_id: row ? row.id : "",
                              itemName: row ? row.name : prev.itemName,
                            }))
                          }
                          onCreated={onItemTypeCreated}
                          storeId={storeId}
                          userId={userId}
                          branchId={itemTypeBranchId}
                          showSnackbar={showSnackbar}
                          error={itemTypeError}
                          disabled={paymentLocked}
                        />
                      </Grid>

                      <Grid item lg={0.75} md={1.5} sm={6} xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Pcs"
                          type="number"
                          id={ORNAMENT_PCS_INPUT_ID}
                          value={currentOrnament.pieces}
                          onChange={(e) => {
                            let value = parseInt(e.target.value) || "";
                            if (value < 0) value = 0;
                            setCurrentOrnament((prev) => ({
                              ...prev,
                              pieces: value === "" ? "" : value,
                            }));
                          }}
                          sx={fieldSx}
                          inputProps={{ min: 0, step: 1 }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "Minus") {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Grid>

                      <Grid item lg={1.25} md={2.5} sm={4} xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Gross (g) *"
                          type="number"
                          value={currentOrnament.grossWeight}
                          inputProps={{
                            "data-skip-enter-tab": "true",
                            // Makes soft keyboards render a "Next" action key
                            // instead of a plain newline/done key.
                            enterKeyHint: "next",
                          }}
                          onChange={(e) =>
                            setCurrentOrnament((prev) => ({
                              ...prev,
                              grossWeight: parseFloat(e.target.value) || 0,
                            }))
                          }
                          onKeyDown={(e) => {
                            // keyCode/which as well as key: some Android IMEs
                            // report the action key without a usable e.key.
                            if (
                              e.key === "Enter" ||
                              e.keyCode === 13 ||
                              e.which === 13
                            ) {
                              e.preventDefault();
                              syncNetFromGross();
                              focusAddButton();
                            }
                          }}
                          onBlur={(e) => {
                            // Soft keyboards never reach the handler above: the
                            // Android "Next" key advances focus natively (no
                            // keydown at all) and iOS number pads have no action
                            // key, so on touch devices the blur IS the commit.
                            if (!isTouch) return;
                            const toNet =
                              e.relatedTarget?.dataset?.ornamentNet === "true";
                            // Landing on Net without a preceding tap on it means
                            // the keyboard's "Next" moved us there — that is the
                            // mobile Enter, so treat it exactly like one. A real
                            // tap on Net means the user wants to type their own
                            // figure; leave it alone.
                            const viaNextKey =
                              toNet && Date.now() - netTapAtRef.current > 700;
                            if (toNet && !viaNextKey) return;
                            const filled = syncNetFromGross({
                              requireUntouchedNet: !viaNextKey,
                            });
                            // Mirror the desktop Enter: park on Add so the
                            // auto-filled net can't be typed over by accident.
                            if (filled && viaNextKey) focusAddButton();
                          }}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid item lg={1.25} md={2.5} sm={4} xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Net (g) *"
                          type="number"
                          value={currentOrnament.netWeight}
                          inputProps={{ "data-ornament-net": "true" }}
                          // Distinguishes a real tap on Net from the soft
                          // keyboard's "Next" landing here — see Gross's onBlur.
                          onPointerDown={() => {
                            netTapAtRef.current = Date.now();
                          }}
                          onTouchStart={() => {
                            netTapAtRef.current = Date.now();
                          }}
                          error={
                            (parseFloat(currentOrnament.netWeight) || 0) > 0 &&
                            (parseFloat(currentOrnament.grossWeight) || 0) >
                              0 &&
                            (parseFloat(currentOrnament.netWeight) || 0) >
                              (parseFloat(currentOrnament.grossWeight) || 0)
                          }
                          helperText={
                            (parseFloat(currentOrnament.netWeight) || 0) >
                              (parseFloat(currentOrnament.grossWeight) || 0) &&
                            (parseFloat(currentOrnament.grossWeight) || 0) > 0
                              ? "Net > Gross"
                              : ""
                          }
                          onChange={(e) => {
                            let value = e.target.value;
                            if (value === "") {
                              setCurrentOrnament((prev) => ({
                                ...prev,
                                netWeight: "",
                              }));
                              return;
                            }
                            let num = parseFloat(value);
                            if (!isNaN(num) && num >= 0) {
                              setCurrentOrnament((prev) => ({
                                ...prev,
                                netWeight: num,
                              }));
                            } else if (num < 0) {
                              setCurrentOrnament((prev) => ({
                                ...prev,
                                netWeight: 0,
                              }));
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "Minus") {
                              e.preventDefault();
                            }
                          }}
                          sx={
                            (parseFloat(currentOrnament.netWeight) || 0) >
                              (parseFloat(currentOrnament.grossWeight) || 0) &&
                            (parseFloat(currentOrnament.grossWeight) || 0) > 0
                              ? getFieldSx(true)
                              : fieldSx
                          }
                        />
                      </Grid>

                      <Grid item lg={1.45} md={2.5} sm={4} xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Value (₹) *"
                          type="number"
                          value={
                            currentOrnament.value === "" ||
                            currentOrnament.value == null
                              ? ""
                              : currentOrnament.value
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "") {
                              // Cleared → resume auto-calculation.
                              setValueEdited(false);
                              setCurrentOrnament((p) => ({ ...p, value: "" }));
                              return;
                            }
                            const n = parseFloat(v);
                            if (!isNaN(n) && n >= 0) {
                              setValueEdited(true);
                              setCurrentOrnament((p) => ({ ...p, value: n }));
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "Minus")
                              e.preventDefault();
                          }}
                          inputProps={{ min: 0, step: "any" }}
                          sx={{
                            ...fieldSx,
                            "& .MuiInputBase-input": {
                              color: BLUE,
                              fontWeight: 700,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item lg={1.5} md={3} sm={12} xs={12}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          startIcon={
                            editingIndex !== null ? <EditIcon /> : <AddIcon />
                          }
                          onClick={addOrnament}
                          sx={{
                            py: 1.1,
                            borderRadius: "8px",
                            fontWeight: 700,
                            boxShadow: "none",
                            fontSize: "0.8rem",
                          }}
                          ref={addOrnamentBtnRef}
                        >
                          {editingIndex !== null ? "Update" : "Add"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Ornaments Table */}
                  {ornaments.length === 0 ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        border: errors.ornaments
                          ? `2px dashed ${ERROR_C}`
                          : `2px dashed ${BORDER}`,
                        borderRadius: "10px",
                        bgcolor: errors.ornaments ? ERROR_BG : BG,
                      }}
                    >
                      <Typography
                        sx={{
                          color: errors.ornaments ? ERROR_C : INK_MUTED,
                          fontSize: "0.85rem",
                        }}
                      >
                        {errors.ornaments
                          ? "⚠️ At least one ornament is required"
                          : "No ornaments added yet. Fill the form above to add items."}
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer
                      sx={{
                        maxHeight: 360,
                        borderRadius: "10px",
                        border: `1px solid ${BORDER}`,
                        overflow: "auto",
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {[
                              "#",
                              "Metal",
                              "Item",
                              "Pcs",
                              ...(!isMobile ? ["Gross(g)"] : []),
                              "Net(g)",
                              ...(!isMobile ? ["Purity%"] : []),
                              "Value(₹)",
                              "",
                            ].map((h) => (
                              <TableCell
                                key={h}
                                sx={{
                                  bgcolor: INK,
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: "0.7rem",
                                  letterSpacing: "0.06em",
                                  textTransform: "uppercase",
                                  py: 1.25,
                                  px: 1.5,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ornaments?.map((item, idx) => (
                            <TableRow
                              key={idx}
                              sx={{
                                bgcolor: idx % 2 === 0 ? SURFACE : BG,
                                "&:hover": { bgcolor: BLUE_SUBTLE },
                                transition: "background 0.1s",
                              }}
                            >
                              <TableCell
                                sx={{
                                  px: 1.5,
                                  color: INK_MUTED,
                                  fontSize: "0.78rem",
                                }}
                              >
                                {idx + 1}
                              </TableCell>
                              <TableCell sx={{ px: 1.5 }}>
                                <Chip
                                  label={item.metal}
                                  size="small"
                                  sx={{
                                    bgcolor: BLUE_LIGHT,
                                    color: BLUE_DARK,
                                    border: `1px solid ${BLUE_MED}44`,
                                    fontSize: "0.7rem",
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  px: 1.5,
                                  color: INK,
                                  fontWeight: 600,
                                  fontSize: "0.82rem",
                                }}
                              >
                                {item.itemName}
                              </TableCell>
                              <TableCell sx={{ px: 1.5, color: INK_SOFT }}>
                                {item.pieces}
                              </TableCell>
                              {!isMobile && (
                                <TableCell
                                  sx={{
                                    px: 1.5,
                                    color: INK_MUTED,
                                    fontSize: "0.78rem",
                                  }}
                                >
                                  {parseFloat(item.grossWeight || 0).toFixed(3)}
                                </TableCell>
                              )}
                              <TableCell
                                sx={{ px: 1.5, color: INK, fontWeight: 600 }}
                              >
                                {parseFloat(item.netWeight || 0).toFixed(3)}
                              </TableCell>
                              {!isMobile && (
                                <TableCell
                                  sx={{
                                    px: 1.5,
                                    color: INK_MUTED,
                                    fontSize: "0.78rem",
                                  }}
                                >
                                  {parseFloat(item.purity || 0).toFixed(2)}%
                                </TableCell>
                              )}
                              <TableCell
                                sx={{
                                  px: 1.5,
                                  color: BLUE_DARK,
                                  fontWeight: 700,
                                }}
                              >
                                ₹
                                {Number(item.value || 0).toLocaleString(
                                  "en-IN",
                                )}
                              </TableCell>
                              <TableCell sx={{ px: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => editOrnament(idx)}
                                  sx={{
                                    color: INFO_C,
                                    "&:hover": { bgcolor: `${INFO_C}14` },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => deleteOrnament(idx)}
                                  sx={{
                                    color: ERROR_C,
                                    "&:hover": { bgcolor: ERROR_BG },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: BLUE_PALE }}>
                            <TableCell
                              colSpan={isMobile ? 5 : 7}
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: INK_SOFT,
                                fontSize: "0.82rem",
                                px: 1.5,
                              }}
                            >
                              Total Ornament Value
                            </TableCell>
                            <TableCell
                              colSpan={2}
                              sx={{
                                fontWeight: 800,
                                color: BLUE_DARK,
                                fontSize: "0.9rem",
                                px: 1.5,
                              }}
                            >
                              ₹
                              {Number(formData.itemValue || 0).toLocaleString(
                                "en-IN",
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </SectionCard>
  );
};

export default OrnamentsSection;
